import React, { useCallback, useEffect, useState } from "react";
import { emitToast } from "./Toast";
import StatusBadge from "./StatusBadge";
import { diffMin, fmtMin } from "./dateUtil";
import ErrorBar from "./ErrorBar";
import EmptyState from "./EmptyState";
import Loader from "./Loader";
import ConfirmDialog from "./ConfirmDialog";
import Pager, { usePager } from "./Pager";
import apiErrorMessage from "./apiError";
import { jsonHeaders } from "./csrf";

// 社員の申請3種（休み・時間訂正・残業）を1つにまとめた共通の申請履歴。
// 申請ハブ(ApplyHubPage)でタブの下に常設し、タブを切り替えても表示は変わらない
// （＝どのタブでも全種別の履歴が同じ順で見える）。各種別の /mine/ を並列取得して
// 日付降順にマージするだけで、BE 側に統合APIは持たない（YAGNI）。
// 取消は行が属する種別ごとの cancel エンドポイントへ振り分ける。
const API_URL = process.env.REACT_APP_API_URL || "";

// 休みの種別ラベル（BEは paid/absence/special を返す）。
const LEAVE_TYPE_LABEL = { paid: "有給", absence: "欠勤", special: "特別" };

// 出勤–退勤ペアを「09:40 - 19:00」の形に（相方が無い側は「-」）。時間訂正の内容表示用。
const pair = (i, o) => `${i || "-"} - ${o || "-"}`;

// ── 3種の /mine/ 応答を、共通の1行に正規化する ──
// key   : 種別をまたいで一意（種別-id）。React の key と取消対象の同定に使う。
// sortDate : 並び替え用の "YYYY-MM-DD"（無ければ ""）。
// dateLabel: 表示用の日付（休みは期間のことがある）。
// detail   : 種別ごとの1行要約（内容列）。
// cancelUrl: 取消の送信先（種別ごとに違う）。
const normalizeLeave = (r) => {
  const period = r.date_from === r.date_to ? r.date_from : `${r.date_from} 〜 ${r.date_to}`;
  const typeLabel = LEAVE_TYPE_LABEL[r.type] || r.type;
  return {
    key: `leave-${r.id}`,
    id: r.id,
    kindLabel: "休み",
    sortDate: r.date_from || "",
    dateLabel: period,
    detail: typeLabel,
    reason: r.reason || "",
    status: r.status,
    cancelUrl: `${API_URL}/api/employee/leave/cancel/`,
    confirmText: `${typeLabel}・${period}`,
  };
};

const normalizeCorrection = (r) => {
  const before = pair(r.in_before, r.out_before);
  const after = pair(r.in_after, r.out_after);
  return {
    key: `correction-${r.id}`,
    id: r.id,
    kindLabel: "時間訂正",
    sortDate: r.target_date || "",
    dateLabel: r.target_date || "-",
    detail: `${before} → ${after}`,
    reason: r.reason || "",
    status: r.status,
    cancelUrl: `${API_URL}/api/employee/time-correction/cancel/`,
    confirmText: `${r.target_date || ""}の訂正（${before} → ${after}）`,
  };
};

const normalizeOvertime = (r) => {
  const span = r.start && r.end ? `${r.start}〜${r.end}（${fmtMin(diffMin(r.start, r.end))}）` : "-";
  return {
    key: `overtime-${r.id}`,
    id: r.id,
    kindLabel: "残業",
    sortDate: r.date || "",
    dateLabel: r.date || "-",
    detail: span,
    reason: r.reason || "",
    status: r.status,
    cancelUrl: `${API_URL}/api/employee/overtime/cancel/`,
    confirmText: `${r.date || ""}（${r.start}〜${r.end}）の残業`,
  };
};

// reloadKey が変わるたびに再取得する（親＝ApplyHubPage が申請成立後に増やす）。
export default function ApplyHistory({ reloadKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // 取消の確認ダイアログ（対象の行。null=閉じている）。誤操作防止。
  const [confirmRow, setConfirmRow] = useState(null);
  const [canceling, setCanceling] = useState(false);
  // 表示件数を超えたらページャーを出す（1ページぶんだけ描画）。再読込時は先頭ページへ戻す。
  const { page, setPage, pageRows, pageCount } = usePager(rows);
  useEffect(() => { setPage(1); }, [reloadKey, setPage]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const responses = await Promise.all([
        fetch(`${API_URL}/api/employee/leave/mine/`, { credentials: "include" }),
        fetch(`${API_URL}/api/employee/time-correction/mine/`, { credentials: "include" }),
        fetch(`${API_URL}/api/employee/overtime/mine/`, { credentials: "include" }),
      ]);
      if (responses.some((r) => r.status === 401)) { setError("ログインが必要です。"); return; }
      if (responses.some((r) => r.status === 403)) { setError("社員プロフィールがありません。管理者にお問い合わせください。"); return; }
      if (responses.some((r) => !r.ok)) throw new Error("取得に失敗しました");
      const [leaveData, corrData, otData] = await Promise.all(responses.map((r) => r.json()));
      const merged = [
        ...(leaveData.results || []).map(normalizeLeave),
        ...(corrData.results || []).map(normalizeCorrection),
        ...(otData.results || []).map(normalizeOvertime),
      ];
      // 日付降順（新しい順）。同日は id 降順で安定させる。
      merged.sort((a, b) => {
        if (a.sortDate !== b.sortDate) return a.sortDate < b.sortDate ? 1 : -1;
        return b.id - a.id;
      });
      setRows(merged);
    } catch (e) {
      setError("申請履歴の取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll, reloadKey]);

  // 確認ダイアログで「取り消す」を押したときの実処理。行の種別に応じた cancelUrl へ送る。
  const doCancel = async () => {
    if (!confirmRow) return;
    const target = confirmRow;
    setCanceling(true); setError("");
    try {
      const res = await fetch(target.cancelUrl, {
        method: "POST", credentials: "include",
        headers: jsonHeaders(),
        body: JSON.stringify({ id: target.id }),
      });
      if (!res.ok) { setError(await apiErrorMessage(res, "取消に失敗しました。")); return; }
      setRows((prev) => prev.filter((r) => r.key !== target.key));
      emitToast("申請を取り消しました。");
    } catch (e) {
      setError("取消に失敗しました。");
    } finally {
      setCanceling(false);
      setConfirmRow(null);
    }
  };

  return (
    <>
      {error && <ErrorBar message={error} onClose={() => setError("")} />}

      <h2 className="mp-h2">申請履歴</h2>
      {loading ? (
        <Loader />
      ) : rows.length === 0 ? (
        <EmptyState>申請がありません</EmptyState>
      ) : (
        <>
          {/* デスクトップ：テーブル（種別｜日付｜内容｜理由｜状態｜取消） */}
          <div className="mp-tablewrap mp-only-desktop">
            <table className="mp-table">
              <thead>
                <tr><th>種別</th><th>日付</th><th>内容</th><th>理由</th><th>状態</th><th></th></tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                    <tr key={r.key}>
                      <td className="mp-nowrap">{r.kindLabel}</td>
                      <td className="mp-nowrap">{r.dateLabel}</td>
                      <td className="mp-nowrap">{r.detail}</td>
                      <td className="mp-muted"><div className="mp-cellwrap">{r.reason || "-"}</div></td>
                      <td><StatusBadge status={r.status} /></td>
                      <td>
                        <div className="mp-actions">
                          {r.status === "pending"
                            ? <button className="mp-btn mp-danger mp-sm" onClick={() => setConfirmRow(r)}>取消</button>
                            : <span className="mp-muted">-</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* モバイル：カード（種別＋状態／日付・内容／理由／取消は右）。 */}
          <div className="mp-lv-list mp-only-mobile">
            {pageRows.map((r) => (
                <div className="mp-lv-item" key={r.key}>
                  <div className="mp-lv-main">
                    <div className="mp-lv-head">
                      <span className="mp-lv-type">{r.kindLabel}</span>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="mp-lv-period">{r.dateLabel}・{r.detail}</div>
                    {r.reason ? <div className="mp-lv-reason">{r.reason}</div> : null}
                  </div>
                  {r.status === "pending" ? (
                    <button className="mp-btn mp-danger mp-sm" onClick={() => setConfirmRow(r)}>取消</button>
                  ) : null}
                </div>
              ))}
          </div>

          <Pager page={page} pageCount={pageCount} onChange={setPage} />
        </>
      )}

      {/* 取消の確認ダイアログ（誤操作防止）。背景クリック / Esc / やめる で閉じる。 */}
      <ConfirmDialog
        open={!!confirmRow}
        ariaLabel="申請の取消確認"
        title="この申請を取り消しますか？"
        onClose={() => setConfirmRow(null)}
        onConfirm={doCancel}
        confirmLabel={canceling ? "取消中…" : "取り消す"}
        confirmDisabled={canceling}
      >
        {confirmRow && `${confirmRow.kindLabel}｜${confirmRow.confirmText} を取り消します。取り消すと履歴から削除され、元に戻せません。`}
      </ConfirmDialog>
    </>
  );
}
